import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Check, ExternalLink, FlaskConical, RefreshCw, Save, X } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { mediaUrl } from '../../utils/mediaUrl';
import {
  BackLink, Button, Card, CardTitle, CheckLabel, Checks, Container, Empty, Field, HintNote, Input, Message,
  ModalActions, SourceBox, SourceLink, Textarea, Title, WideField,
} from '../admin/ui';
import { INTEREST_CATEGORIES } from '../../constants/doctorProfile';
import * as Api from './PersonAdminApi';
import {
  FIELD_GROUPS, PROFESSIONAL_STATUS_VALUES, changedPayload, editableFields,
  errorText, formStateOf, interestLabel, originLabel, statusLabel,
} from './personAdminModel';

// The admin person detail (plan §7.7 item 11, P11): the profile field by field with its provenance
// and sources (D15), the open change sets with each proposed value next to the current one (per-field
// accept/reject for RESEARCH and IMPORT, explicit save), the person's spots with role and state
// (linking to the program screen), the photo upload, and AI research. On an account holder only
// internal notes and position are editable (D33): the profile is the person's own and admin changes
// reach them with their next invitation (an ADMIN change set is accepted at creation and rides along
// automatically); a photo the admin uploads is an ADMIN change set too, so it does the same. "Apply
// directly" (owner decision 2026-10-20) is the exception: after one confirmation every field but the
// contact email (the login) is written at once, PUT ?force=true, and the provenance says so.
//
// Every call goes through ./PersonAdminApi; a 4xx/5xx answers a plain-text body the admin should read.

const formatMoment = (intl, iso) => (iso
  ? intl.formatDate(new Date(iso), { dateStyle: 'medium', timeStyle: 'short' })
  : null);

const valueText = (t, key, value) => {
  if (key === 'professionalStatuses') return (value || []).map((s) => statusLabel(t, s)).join(', ');
  if (key === 'interests') return (value || []).map((i) => interestLabel(t, i)).join(', ');
  return value == null ? '' : String(value);
};

const Sources = ({ sources }) => (
  <SourceBox>
    <strong><FormattedMessage id="personAdmin.source" defaultMessage="Source" /></strong>
    {sources && sources.length ? sources.map((source, index) => (source.url ? (
      <SourceLink key={index} href={source.url} target="_blank" rel="noreferrer">
        <ExternalLink size={12} />{source.for ? `${source.for}: ` : ''}{source.title || source.url}
      </SourceLink>
    ) : (
      <div key={index}>{source.for ? `${source.for}: ` : ''}{source.title || <FormattedMessage id="personAdmin.source.input" defaultMessage="Organizer input" />}</div>
    ))) : <div><FormattedMessage id="personAdmin.source.none" defaultMessage="No source recorded" /></div>}
  </SourceBox>
);

const Provenance = ({ intl, t, provenance }) => {
  if (!provenance) {
    return (
      <div style={{ fontSize: '.72rem', color: '#94a3b8', marginTop: '.25rem' }}>
        <FormattedMessage id="personAdmin.provenance.none" defaultMessage="No recorded change (value from before the migration)" />
      </div>
    );
  }
  return (
    <div style={{ marginTop: '.3rem' }}>
      <div style={{ fontSize: '.72rem', color: '#64748b' }}>
        <FormattedMessage id="personAdmin.provenance.from" defaultMessage="From {origin}{date}"
          values={{
            origin: originLabel(t, provenance.origin),
            date: provenance.appliedAt ? ` — ${formatMoment(intl, provenance.appliedAt)}` : '',
          }} />
        {provenance.forced && <> · <FormattedMessage id="personAdmin.provenance.forced" defaultMessage="applied directly, without asking the person" /></>}
      </div>
      {provenance.sources && provenance.sources.length > 0 && <Sources sources={provenance.sources} />}
    </div>
  );
};

const PersonDetail = () => {
  const { id } = useParams();
  const personId = Number(id);
  const intl = useIntl();
  const { language } = useLanguage();
  const t = useCallback((key, def, values) => intl.formatMessage({ id: key, defaultMessage: def }, values), [intl]);

  const [person, setPerson] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  // "Apply directly" on an account holder (owner decision 2026-10-20): every field but the contact
  // email (the login) becomes editable, and Save first asks for the one confirmation.
  const [force, setForce] = useState(false);
  const [confirmForce, setConfirmForce] = useState(false);
  const [decisions, setDecisions] = useState({});
  const [savingSet, setSavingSet] = useState(null);
  const [researchBusy, setResearchBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const seq = useRef(0);
  const photoInput = useRef(null);

  // Blank current fields are pre-selected ACCEPT, as today (design §4.2.3 step 2).
  const load = useCallback(async () => {
    const token = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const detail = await Api.fetchPerson(personId);
      if (seq.current !== token) return;
      setPerson(detail);
      setForm(formStateOf(detail.fields));
      setDecisions(initialDecisions(detail.changeSets));
      Api.fetchResearchJobs(personId).then((all) => { if (seq.current === token) setJobs(all || []); }).catch(() => {});
    } catch (err) {
      if (seq.current === token) setError(err);
    } finally {
      if (seq.current === token) setLoading(false);
    }
  }, [personId]);

  useEffect(() => { load(); }, [load]);

  const hasAccount = !!person?.account;
  const editableKeys = useMemo(() => new Set(editableFields(hasAccount, force).map((f) => f.key)), [hasAccount, force]);
  const groups = useMemo(() => FIELD_GROUPS
    .map((group) => ({ ...group, fields: group.fields.filter((field) => editableKeys.has(field.key)) }))
    .filter((group) => group.fields.length > 0), [editableKeys]);

  const openSets = useMemo(() => decorate(person?.changeSets)
    .filter((set) => set.status === 'OPEN' || set.status === 'SENT'), [person]);
  const pendingCount = useMemo(() => openSets
    .reduce((sum, set) => sum + set.fields.filter((f) => f.canBeDecided).length, 0), [openSets]);

  const change = useCallback((key, value) => setForm((current) => ({ ...current, [key]: value })), []);
  const toggleList = useCallback((key, item) => setForm((current) => {
    const list = current[key] || [];
    return { ...current, [key]: list.includes(item) ? list.filter((v) => v !== item) : [...list, item] };
  }), []);

  const forcing = hasAccount && force;

  const save = useCallback(async (confirmed = false) => {
    const payload = changedPayload(form, person.fields);
    if (Object.keys(payload).length === 0) {
      setMessage({ error: false, text: t('personAdmin.edit.unchanged', 'Nothing was changed.') });
      return;
    }
    if (forcing && !confirmed) {
      setConfirmForce(true);
      return;
    }
    setConfirmForce(false);
    setSaving(true);
    setMessage(null);
    try {
      const updated = forcing
        ? await Api.savePerson(personId, payload, { force: true })
        : await Api.savePerson(personId, payload);
      setPerson(updated);
      setForm(formStateOf(updated.fields));
      setMessage({ error: false, text: forcing
        ? t('personAdmin.edit.savedForced', 'Saved. The changes were applied directly to the person’s profile.')
        : hasAccount
          ? t('personAdmin.edit.savedAccount', 'Saved. Internal notes and position apply at once; any other profile change rides along with the person’s next invitation.')
          : t('personAdmin.edit.saved', 'Saved. The change was applied to the person.') });
    } catch (err) {
      setMessage({ error: true, text: errorText(err, t('personAdmin.edit.failed', 'Could not save the change.')) });
    } finally {
      setSaving(false);
    }
  }, [form, person, personId, hasAccount, forcing, t]);

  // Switching "Apply directly" on or off starts from the person's values again, so no edit of a field
  // the other mode does not offer is sent along.
  const toggleForce = useCallback(() => {
    setForce((current) => !current);
    setConfirmForce(false);
    setForm(formStateOf(person.fields));
  }, [person]);

  const decide = useCallback((setId, key, decision) => setDecisions((current) => ({
    ...current, [setId]: { ...(current[setId] || {}), [key]: decision },
  })), []);

  const saveDecisions = useCallback(async (set) => {
    const chosen = Object.fromEntries(Object.entries(decisions[set.id] || {}).filter(([, decision]) => decision));
    if (Object.keys(chosen).length === 0) return;
    setSavingSet(set.id);
    setMessage(null);
    try {
      await Api.decideChangeSet(personId, set.id, chosen);
      await load();
      setMessage({ error: false, text: t('personAdmin.changeSet.saved', 'The decisions were saved and applied.') });
    } catch (err) {
      setMessage({ error: true, text: errorText(err, t('personAdmin.changeSet.failed', 'Could not save the decisions.')) });
    } finally {
      setSavingSet(null);
    }
  }, [decisions, personId, load, t]);

  const closeSet = useCallback(async (set) => {
    setSavingSet(set.id);
    setMessage(null);
    try {
      await Api.closeChangeSet(personId, set.id);
      await load();
      setMessage({ error: false, text: t('personAdmin.changeSet.closed', 'The change set was closed without applying the rest.') });
    } catch (err) {
      setMessage({ error: true, text: errorText(err, t('personAdmin.changeSet.closeFailed', 'Could not close the change set.')) });
    } finally {
      setSavingSet(null);
    }
  }, [personId, load, t]);

  const startResearch = useCallback(async () => {
    setResearchBusy(true);
    setMessage(null);
    try {
      await Api.startResearch(personId, 'GENERAL');
      await load();
      setMessage({ error: false, text: t('personAdmin.research.started', 'Research was started. Results appear as an open change set.') });
    } catch (err) {
      setMessage({ error: true, text: errorText(err, t('personAdmin.research.failed', 'Could not start research.')) });
    } finally {
      setResearchBusy(false);
    }
  }, [personId, load, t]);

  const uploadPhoto = useCallback(async (file) => {
    if (!file) return;
    setPhotoBusy(true);
    setMessage(null);
    try {
      await Api.uploadPersonPhoto(personId, file);
      await load();
      setMessage({ error: false, text: hasAccount
        ? t('personAdmin.photo.proposed', 'Photo uploaded. This person has an account, so the photo is proposed and rides along with their next invitation.')
        : t('personAdmin.photo.saved', 'Photo uploaded and stored on the person.') });
    } catch (err) {
      setMessage({ error: true, text: errorText(err, t('personAdmin.photo.failed', 'Could not upload the photo.')) });
    } finally {
      setPhotoBusy(false);
    }
  }, [personId, hasAccount, load, t]);

  const onPhotoPicked = useCallback((event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    uploadPhoto(file);
  }, [uploadPhoto]);

  if (loading && !person) {
    return <Container><Empty><FormattedMessage id="personAdmin.loading" defaultMessage="Loading people…" /></Empty></Container>;
  }
  if (error && !person) {
    return (
      <Container>
        <BackLink to="/admin/people"><FormattedMessage id="personAdmin.detail.back" defaultMessage="Back to the people" /></BackLink>
        <Message error role="alert">{errorText(error, t('personAdmin.detail.failed', 'Could not load this person.'))}</Message>
      </Container>
    );
  }

  const research = person.research;
  const latestJob = jobs[0] || null;
  const photoUrl = person.fields.photoUrl?.value;

  return (
    <Container>
      <BackLink to="/admin/people"><FormattedMessage id="personAdmin.detail.back" defaultMessage="Back to the people" /></BackLink>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div>
          <Title>{person.programName}</Title>
          <div style={{ color: '#64748b', fontSize: '.85rem', margin: '.25rem 0 1.5rem' }}>
            {person.account
              ? <FormattedMessage id="personAdmin.detail.hasAccount" defaultMessage="Has an account: {email}" values={{ email: person.account.email }} />
              : <FormattedMessage id="personAdmin.detail.noAccount" defaultMessage="No account" />}
            {' · '}
            <FormattedMessage id="personAdmin.detail.origin" defaultMessage="Added as {origin}"
              values={{ origin: originLabel(t, person.origin) }} />
            {person.updatedAt && <> {' · '}<FormattedMessage id="personAdmin.detail.updated"
              defaultMessage="Last changed {date}" values={{ date: formatMoment(intl, person.updatedAt) }} /></>}
          </div>
        </div>
        <Button type="button" secondary disabled={researchBusy} onClick={startResearch}>
          <FlaskConical size={16} />
          {jobs.length > 0
            ? <FormattedMessage id="personAdmin.research.again" defaultMessage="Research again" />
            : <FormattedMessage id="personAdmin.research.start" defaultMessage="Start research" />}
        </Button>
      </div>

      {message && <Message error={message.error} role={message.error ? 'alert' : 'status'}>{message.text}</Message>}

      <Card>
        <CardTitle><FormattedMessage id="personAdmin.profile.title" defaultMessage="Profile" /></CardTitle>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.25rem' }}>
          {photoUrl ? (
            <img src={mediaUrl(photoUrl)} alt="" referrerPolicy="no-referrer"
              style={{ width: '7rem', height: '7rem', objectFit: 'cover', border: '1px solid #cbd5e1', background: '#f8fafc' }} />
          ) : (
            <div style={{ width: '7rem', height: '7rem', border: '1px solid #cbd5e1', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '.78rem' }}>
              <FormattedMessage id="personAdmin.photo.none" defaultMessage="No photo" />
            </div>
          )}
          <div>
            <input ref={photoInput} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }}
              aria-label={t('personAdmin.photo.choose', 'Choose a photo file')} onChange={onPhotoPicked} />
            <Button type="button" secondary disabled={photoBusy} onClick={() => photoInput.current && photoInput.current.click()}>
              {photoBusy
                ? <FormattedMessage id="personAdmin.photo.uploading" defaultMessage="Uploading…" />
                : <FormattedMessage id="personAdmin.photo.upload" defaultMessage="Upload photo" />}
            </Button>
            <HintNote style={{ display: 'block', margin: '.4rem 0 0' }}>
              <FormattedMessage id="personAdmin.photo.hint" defaultMessage="JPG, PNG or WebP, up to 10 MB." />
            </HintNote>
          </div>
        </div>
        {FIELD_GROUPS.map((group) => (
          <section key={group.id} style={{ marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '.82rem', textTransform: 'uppercase', letterSpacing: '.04em', color: '#64748b', margin: '0 0 .5rem' }}>
              <FormattedMessage id={`personAdmin.group.${group.id}`} defaultMessage={group.id} />
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(18rem, 1fr))', gap: '.75rem 1.25rem' }}>
              {group.fields.map((field) => {
                const view = person.fields[field.key] || { value: null, provenance: null };
                const text = valueText(t, field.key, view.value);
                return (
                  <div key={field.key}>
                    <div style={{ fontSize: '.78rem', fontWeight: 600, color: '#334155' }}>
                      <FormattedMessage id={`personAdmin.field.${field.key}`} defaultMessage={field.key} />
                    </div>
                    <div style={{ fontSize: '.85rem', color: text ? '#0f172a' : '#94a3b8', whiteSpace: 'pre-wrap' }}>
                      {text || <FormattedMessage id="personAdmin.none" defaultMessage="—" />}
                    </div>
                    <Provenance intl={intl} t={t} provenance={view.provenance} />
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </Card>

      <Card>
        <CardTitle>
          <FormattedMessage id="personAdmin.changeSet.title" defaultMessage="Open change sets" />
          {pendingCount > 0 && (
            <> · {t('personAdmin.changeSet.pending', '{count} to decide', { count: pendingCount })}</>
          )}
        </CardTitle>
        {openSets.length === 0 ? (
          <Empty style={{ padding: '1rem 0' }}><FormattedMessage id="personAdmin.changeSet.empty" defaultMessage="Nothing waits for a decision." /></Empty>
        ) : (
          openSets.map((set) => (
            <section key={set.id} style={{ borderTop: '1px solid #eef2f7', padding: '1rem 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '.75rem', flexWrap: 'wrap', alignItems: 'baseline' }}>
                <h3 style={{ margin: 0, fontSize: '.95rem' }}>
                  <FormattedMessage id="personAdmin.changeSet.set" defaultMessage="{origin} change set" values={{ origin: originLabel(t, set.origin) }} />
                  {' · '}
                  <span style={{ fontSize: '.78rem', fontWeight: 400, color: '#64748b' }}>{formatMoment(intl, set.createdAt)}</span>
                </h3>
                {set.status === 'SENT' && (
                  <span style={{ fontSize: '.75rem', color: '#92400e', background: '#fef3c7', padding: '.15rem .5rem' }}>
                    <FormattedMessage id="personAdmin.changeSet.sent" defaultMessage="Sent with an invitation — waiting for the person" />
                  </span>
                )}
              </div>

              <div style={{ overflowX: 'auto', marginTop: '.6rem' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.83rem' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: '#475569' }}>
                      <Th><FormattedMessage id="personAdmin.changeSet.field" defaultMessage="Field" /></Th>
                      <Th><FormattedMessage id="personAdmin.changeSet.current" defaultMessage="Current" /></Th>
                      <Th><FormattedMessage id="personAdmin.changeSet.proposed" defaultMessage="Proposed" /></Th>
                      <Th><FormattedMessage id="personAdmin.changeSet.state" defaultMessage="State" /></Th>
                      <Th><FormattedMessage id="personAdmin.changeSet.decision" defaultMessage="Decision" /></Th>
                    </tr>
                  </thead>
                  <tbody>
                    {set.fields.map((change) => (
                      <tr key={change.id} style={{ borderTop: '1px solid #eef2f7' }}>
                        <Td><FormattedMessage id={`personAdmin.field.${change.field}`} defaultMessage={change.field} /></Td>
                        <Td style={{ color: '#64748b' }}>{valueText(t, change.field, change.currentValue) || '—'}</Td>
                        <Td>
                          {valueText(t, change.field, change.proposedValue) || '—'}
                          {change.canBeDecided && change.confidence != null && (
                            <HintNote> · <FormattedMessage id="personAdmin.changeSet.confidence" defaultMessage="confidence {value}" values={{ value: change.confidence }} /></HintNote>
                          )}
                          {change.canBeDecided && <Sources sources={change.sources} />}
                        </Td>
                        <Td><ChangeStateLabel state={change.state} /></Td>
                        <Td>
                          {change.canBeDecided ? (
                            <div style={{ display: 'flex', gap: '.4rem' }}>
                              <Button type="button" secondary={decisions[set.id]?.[change.field] !== 'ACCEPT'}
                                onClick={() => decide(set.id, change.field, 'ACCEPT')} disabled={savingSet === set.id}>
                                <Check size={14} /><FormattedMessage id="personAdmin.changeSet.accept" defaultMessage="Accept" />
                              </Button>
                              <Button type="button" danger={decisions[set.id]?.[change.field] !== 'REJECT'}
                                onClick={() => decide(set.id, change.field, 'REJECT')} disabled={savingSet === set.id}>
                                <X size={14} /><FormattedMessage id="personAdmin.changeSet.reject" defaultMessage="Reject" />
                              </Button>
                            </div>
                          ) : (
                            <span style={{ color: '#94a3b8' }}>
                              {change.adminDecision
                                ? <FormattedMessage id="personAdmin.changeSet.decidedAdmin" defaultMessage="Decided: {decision}" values={{ decision: change.adminDecision }} />
                                : '—'}
                            </span>
                          )}
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {set.status === 'OPEN' && (
                <ModalActions style={{ marginTop: '.75rem', justifyContent: 'flex-start' }}>
                  <Button type="button" disabled={savingSet === set.id || !hasDecision(decisions[set.id])} onClick={() => saveDecisions(set)}>
                    <Save size={15} /><FormattedMessage id="personAdmin.changeSet.save" defaultMessage="Save decisions" />
                  </Button>
                  <Button type="button" secondary disabled={savingSet === set.id} onClick={() => closeSet(set)}>
                    <X size={15} /><FormattedMessage id="personAdmin.changeSet.close" defaultMessage="Close without applying" />
                  </Button>
                </ModalActions>
              )}
            </section>
          ))
        )}
      </Card>

      <Card>
        <CardTitle><FormattedMessage id="personAdmin.edit.title" defaultMessage="Edit" /></CardTitle>
        {hasAccount ? (
          <>
            <Message warn style={{ marginBottom: '.75rem' }}>
              {force
                ? <FormattedMessage id="personAdmin.edit.forceHint"
                  defaultMessage="Apply directly: your changes are written to the profile at once, without asking the person. The contact email is their login and cannot be changed here." />
                : <FormattedMessage id="personAdmin.edit.accountHint"
                  defaultMessage="This person has an account and edits their own profile. You can change only the internal notes and position here (D33); any other profile change rides along with their next invitation (an ADMIN change set is accepted at creation and applied once they accept it)." />}
            </Message>
            <CheckLabel style={{ marginBottom: '1rem' }}>
              <input type="checkbox" checked={force} onChange={toggleForce} disabled={saving} />
              <FormattedMessage id="personAdmin.edit.force" defaultMessage="Apply directly (without asking the person)" />
            </CheckLabel>
          </>
        ) : (
          <p style={{ margin: '0 0 1rem', fontSize: '.82rem', color: '#64748b' }}>
            <FormattedMessage id="personAdmin.edit.hint"
              defaultMessage="This person has no account, so an edit applies at once and is recorded as an ADMIN change set." />
          </p>
        )}

        {groups.map((group) => (
          <section key={group.id} style={{ marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '.82rem', textTransform: 'uppercase', letterSpacing: '.04em', color: '#64748b', margin: '0 0 .5rem' }}>
              <FormattedMessage id={`personAdmin.group.${group.id}`} defaultMessage={group.id} />
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(18rem, 1fr))', gap: '.9rem' }}>
              {group.fields.map((field) => <EditField key={field.key} field={field} value={form[field.key]}
                onChange={change} onToggle={toggleList} />)}
            </div>
          </section>
        ))}

        {confirmForce && (
          <Message warn role="alertdialog" style={{ marginBottom: '.75rem' }}>
            <div>
              <FormattedMessage id="personAdmin.edit.forceConfirm"
                defaultMessage="This person has their own account. Your changes will overwrite their data directly, without asking them." />
            </div>
            <ModalActions style={{ marginTop: '.6rem', justifyContent: 'flex-start' }}>
              <Button type="button" danger disabled={saving} onClick={() => save(true)}>
                <FormattedMessage id="personAdmin.edit.forceConfirmYes" defaultMessage="Overwrite directly" />
              </Button>
              <Button type="button" secondary disabled={saving} onClick={() => setConfirmForce(false)}>
                <FormattedMessage id="personAdmin.edit.forceConfirmNo" defaultMessage="Cancel" />
              </Button>
            </ModalActions>
          </Message>
        )}

        <ModalActions style={{ justifyContent: 'flex-start' }}>
          <Button type="button" disabled={saving || confirmForce} onClick={() => save(false)}>
            <Save size={16} /><FormattedMessage id="personAdmin.edit.save" defaultMessage="Save" />
          </Button>
          <Button type="button" secondary disabled={saving} onClick={() => { setConfirmForce(false); setForm(formStateOf(person.fields)); }}>
            <FormattedMessage id="personAdmin.edit.reset" defaultMessage="Reset" />
          </Button>
        </ModalActions>
      </Card>

      <Card>
        <CardTitle><FormattedMessage id="personAdmin.spots.title" defaultMessage="Spots on the program" /></CardTitle>
        {person.spots.length === 0 ? (
          <Empty style={{ padding: '1rem 0' }}><FormattedMessage id="personAdmin.spots.empty" defaultMessage="This person is on no program yet." /></Empty>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.85rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#475569' }}>
                  <Th><FormattedMessage id="personAdmin.spots.event" defaultMessage="Event" /></Th>
                  <Th><FormattedMessage id="personAdmin.spots.entry" defaultMessage="Entry" /></Th>
                  <Th><FormattedMessage id="personAdmin.spots.role" defaultMessage="Role" /></Th>
                  <Th><FormattedMessage id="personAdmin.spots.state" defaultMessage="State" /></Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {person.spots.map((spot) => (
                  <tr key={spot.assignmentId} style={{ borderTop: '1px solid #eef2f7', opacity: spot.removedAt ? 0.55 : 1 }}>
                    <Td>{spot.eventName || `#${spot.eventId}`}</Td>
                    <Td>{(language === 'en' ? spot.entryTitleEn : spot.entryTitleDe) || `#${spot.entryId}`}</Td>
                    <Td>{t(`programAdmin.role.${spot.role}`, spot.role)}</Td>
                    <Td>
                      {t(`programAdmin.status.${spot.status}`, spot.status)}
                      {spot.openInvitation && <> · <FormattedMessage id="personAdmin.spots.openInvitation" defaultMessage="open invitation" /></>}
                      {spot.removedAt && <> · <FormattedMessage id="personAdmin.spots.removed" defaultMessage="removed" /></>}
                    </Td>
                    <Td>
                      <Link to={`/admin/program?event=${spot.eventId}&spot=${spot.assignmentId}`}
                        style={{ color: '#0369a1' }}>
                        <FormattedMessage id="personAdmin.spots.open" defaultMessage="Open in program" />
                      </Link>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card>
        <CardTitle><FormattedMessage id="personAdmin.research.title" defaultMessage="AI research" /></CardTitle>
        <p style={{ margin: '0 0 .75rem', fontSize: '.82rem', color: '#64748b' }}>
          <FormattedMessage id="personAdmin.research.summary"
            defaultMessage="{count, plural, =0 {No research job has run for this person.} one {# research job has run.} other {# research jobs have run.}}"
            values={{ count: research.jobs }} />
          {research.latestStatus && (
            <> {' '}<FormattedMessage id="personAdmin.research.latest" defaultMessage="The latest is {status} ({date})."
              values={{ status: research.latestStatus, date: formatMoment(intl, research.latestCreatedAt) }} /></>
          )}
        </p>
        {latestJob?.errorMessage && (
          <Message error role="alert">
            <FormattedMessage id="personAdmin.research.error" defaultMessage="The last job failed: {message}" values={{ message: latestJob.errorMessage }} />
          </Message>
        )}
        {jobs.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.82rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#475569' }}>
                  <Th><FormattedMessage id="personAdmin.research.col.id" defaultMessage="Job" /></Th>
                  <Th><FormattedMessage id="personAdmin.research.col.status" defaultMessage="Status" /></Th>
                  <Th><FormattedMessage id="personAdmin.research.col.created" defaultMessage="Started" /></Th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={job.id} style={{ borderTop: '1px solid #eef2f7' }}>
                    <Td>#{job.id}</Td>
                    <Td>{job.status}</Td>
                    <Td>{formatMoment(intl, job.createdAt) || '—'}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <ModalActions style={{ marginTop: '.75rem', justifyContent: 'flex-start' }}>
          <Button type="button" disabled={researchBusy} onClick={startResearch}>
            <RefreshCw size={15} /><FormattedMessage id="personAdmin.research.start" defaultMessage="Start research" />
          </Button>
        </ModalActions>
      </Card>

      <Card>
        <CardTitle><FormattedMessage id="personAdmin.merge.title" defaultMessage="Merge duplicates" /></CardTitle>
        <p style={{ margin: 0, fontSize: '.82rem', color: '#64748b' }}>
          <FormattedMessage id="personAdmin.merge.note"
            defaultMessage="Merging two people is on the backlog; the backend supports it, the screen does not offer it yet (plan §7.7 item 12)." />
        </p>
      </Card>
    </Container>
  );
};

const Th = ({ children }) => (
  <th style={{ padding: '.4rem .6rem', fontWeight: 700, borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap' }}>{children}</th>
);
const Td = ({ children, style }) => <td style={{ padding: '.4rem .6rem', verticalAlign: 'top', ...style }}>{children}</td>;

const ChangeStateLabel = ({ state }) => (
  <span style={{
    fontSize: '.72rem', fontWeight: 700, padding: '.12rem .45rem',
    background: state === 'PENDING' ? '#f1f5f9' : state === 'REJECTED' ? '#fee2e2' : '#dcfce7',
    color: state === 'PENDING' ? '#475569' : state === 'REJECTED' ? '#991b1b' : '#166534',
  }}>
    <FormattedMessage id={`personAdmin.changeSet.state.${state}`} defaultMessage={state} />
  </span>
);

const EditField = ({ field, value, onChange, onToggle }) => {
  if (field.kind === 'statuses') {
    return (
      <WideField>
        <FormattedMessage id={`personAdmin.field.${field.key}`} defaultMessage={field.key} />
        <Checks>
          {PROFESSIONAL_STATUS_VALUES.map((status) => (
            <CheckLabel key={status}>
              <input type="checkbox" checked={(value || []).includes(status)} onChange={() => onToggle(field.key, status)} />
              <FormattedMessage id={`speaker.role.${status}`} defaultMessage={status} />
            </CheckLabel>
          ))}
        </Checks>
      </WideField>
    );
  }
  if (field.kind === 'interests') {
    return (
      <WideField>
        <FormattedMessage id={`personAdmin.field.${field.key}`} defaultMessage={field.key} />
        {INTEREST_CATEGORIES.map((category) => (
          <div key={category.key} style={{ marginTop: '.4rem' }}>
            <div style={{ fontWeight: 600, fontSize: '.78rem', color: '#475569' }}>
              <FormattedMessage id={category.labelId} defaultMessage={category.labelDefault} />
            </div>
            <Checks>
              {category.interests.map((interest) => (
                <CheckLabel key={interest}>
                  <input type="checkbox" checked={(value || []).includes(interest)} onChange={() => onToggle(field.key, interest)} />
                  <FormattedMessage id={`speaker.interest.${interest}`} defaultMessage={interest} />
                </CheckLabel>
              ))}
            </Checks>
          </div>
        ))}
      </WideField>
    );
  }
  if (field.kind === 'textarea') {
    return (
      <WideField>
        <FormattedMessage id={`personAdmin.field.${field.key}`} defaultMessage={field.key} />
        <Textarea value={value || ''} onChange={(e) => onChange(field.key, e.target.value)} />
      </WideField>
    );
  }
  return (
    <Field>
      <FormattedMessage id={`personAdmin.field.${field.key}`} defaultMessage={field.key} />
      <Input value={value || ''} onChange={(e) => onChange(field.key, e.target.value)} />
    </Field>
  );
};

// A field can be decided when the backend still shows it as PENDING (no admin decision yet); a set
// the admin already decided (an ADMIN set on an account holder) is waiting for the invitation only.
function decorate(changeSets) {
  return (changeSets || []).map((set) => ({
    ...set,
    fields: set.fields.map((change) => ({ ...change, canBeDecided: set.status === 'OPEN' && change.adminDecision == null })),
  }));
}

function initialDecisions(changeSets) {
  const out = {};
  for (const set of decorate(changeSets)) {
    const chosen = {};
    for (const change of set.fields) {
      if (change.canBeDecided && isBlank(change.currentValue)) chosen[change.field] = 'ACCEPT';
    }
    if (Object.keys(chosen).length) out[set.id] = chosen;
  }
  return out;
}

const isBlank = (value) => value == null || value === '' || (Array.isArray(value) && value.length === 0);
const hasDecision = (chosen) => !!chosen && Object.values(chosen).some(Boolean);

export default PersonDetail;
